import type {
  SequentialMarginalKpiStepper,
  SequentialMarginalKpis,
} from '@shared/helpers/actions/per-action-proposal-kpi.helpers';

/**
 * Off-render-path runner for the sequential per-action KPI prefix series (DEV-3703).
 *
 * The full series runs one compute-pipeline pass per action — tens of seconds on large
 * proposals — so running it synchronously in the render path froze the app. Here each
 * pass runs in its own macrotask, so input and socket events are handled between passes,
 * and a burst of store updates (multiplayer saves) only restarts one debounced session.
 */

type CacheEntry = {
  deps: readonly unknown[];
  /** `null` marks a failed series: don't reschedule until the deps change. */
  marginals: SequentialMarginalKpis | null;
};

type Session = {
  deps: readonly unknown[];
  subscribers: Set<() => void>;
  timeoutId: ReturnType<typeof setTimeout>;
};

/** Latest finished series per `versionId:proposalId`, valid while `deps` match. */
const marginalsCache = new Map<string, CacheEntry>();
const sessions = new Map<string, Session>();

/** Coalesces bursts of store updates into one session restart. */
const FIRST_STEP_DELAY_MS = 300;

const depsEqual = (a: readonly unknown[], b: readonly unknown[]): boolean =>
  a.length === b.length && a.every((dep, index) => Object.is(dep, b[index]));

const cancelSession = (key: string): void => {
  const session = sessions.get(key);
  if (!session) return;
  clearTimeout(session.timeoutId);
  sessions.delete(key);
};

export const readCachedSequentialMarginalKpis = (
  key: string,
  deps: readonly unknown[],
): SequentialMarginalKpis | 'failed' | 'missing' => {
  const entry = marginalsCache.get(key);
  if (!entry || !depsEqual(entry.deps, deps)) return 'missing';
  return entry.marginals ?? 'failed';
};

/**
 * Computes the prefix series for {@link key} off the render path and caches it by
 * {@link deps}; {@link onComplete} fires once {@link readCachedSequentialMarginalKpis}
 * has data. A session with stale deps is cancelled and replaced; a session with the
 * same deps is shared. Returns an unsubscribe function — the session is cancelled
 * when its last subscriber leaves.
 */
export const scheduleSequentialMarginalKpis = (
  key: string,
  deps: readonly unknown[],
  createStepper: () => SequentialMarginalKpiStepper,
  onComplete: () => void,
): (() => void) => {
  const existing = sessions.get(key);

  if (existing && depsEqual(existing.deps, deps)) {
    existing.subscribers.add(onComplete);
  } else {
    if (existing) cancelSession(key);

    const session: Session = {
      deps,
      subscribers: new Set([onComplete]),
      timeoutId: setTimeout(() => runStep(), FIRST_STEP_DELAY_MS),
    };
    // Created lazily inside the debounced first step so cancelled bursts never
    // build the (recipe-stamp) compute context at all.
    let stepper: SequentialMarginalKpiStepper | null = null;

    const runStep = (): void => {
      try {
        stepper ??= createStepper();
        if (stepper.step() === 'pending') {
          session.timeoutId = setTimeout(runStep, 0);
          return;
        }
      } catch (error) {
        // Mark the series failed so subscribers leave the loading state and
        // the hook does not reschedule until the deps change; rethrow so the
        // global handler (Sentry) still sees the error.
        marginalsCache.set(key, { deps, marginals: null });
        sessions.delete(key);
        session.subscribers.forEach((notify) => notify());
        throw error;
      }
      marginalsCache.set(key, { deps, marginals: stepper.getMarginals() });
      sessions.delete(key);
      session.subscribers.forEach((notify) => notify());
    };

    sessions.set(key, session);
  }

  return () => {
    const session = sessions.get(key);
    if (!session || !session.subscribers.delete(onComplete)) return;
    if (session.subscribers.size > 0) return;
    // Deferred: React replaces an effect as cleanup-then-setup in one commit, so an
    // immediate cancel here would kill a session the very next effect re-joins.
    setTimeout(() => {
      const current = sessions.get(key);
      if (current === session && current.subscribers.size === 0) {
        cancelSession(key);
      }
    }, 0);
  };
};

/** Test-only: drop all cached series and cancel running sessions. */
export const resetSequentialMarginalKpisForTests = (): void => {
  marginalsCache.clear();
  for (const key of [...sessions.keys()]) cancelSession(key);
};
